import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  TextField,
  FormLabel,
  Grid,
  IconButton,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { HighlightOff as HighlightOffIcon } from "@material-ui/icons";
import useTranslation from "Hooks/useTranslation";
import MmsApiService from "Services/MMS/mms-api.service";
import AlertToastr from "utils/alert";

const PlusIcon = ({ style }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.6667 2H3.33333C2.59333 2 2 2.6 2 3.33333V12.6667C2 13.4 2.59333 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V3.33333C14 2.6 13.4 2 12.6667 2ZM12.6667 12.6667H3.33333V3.33333H12.6667V12.6667ZM8.66667 11.3333H7.33333V8.66667H4.66667V7.33333H7.33333V4.66667H8.66667V7.33333H11.3333V8.66667H8.66667V11.3333Z"
      fill="currentColor"
    />
  </svg>
);

const OPERATOR_OPTIONS = [
  { value: "biggerThan", label: "Bigger Than" },
  { value: "smallerThan", label: "Smaller Than" },
  { value: "equals", label: "Equals" },
];

const AddEditMetricModal = ({
  isOpen,
  isOpenChangeHandler,
  editingData,
  onSuccess,
}) => {
  const translation = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [metricData, setMetricData] = useState({
    name: "",
    channelName: "",
    unit: "",
    channelOperator: "biggerThan",
    channelValue: 0,
  });
  const [physicalUnits, setPhysicalUnits] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [conditionalChannels, setConditionalChannels] = useState([]);

  const addConditionalChannel = () => {
    const newChannel = {
      conditionChannelName: "",
      unit: "",
      conditionChannelOperator: "biggerThan",
      conditionChannelValue: 0,
    };
    setConditionalChannels((prev) => [...prev, newChannel]);
  };

  const removeConditionalChannel = (index) => {
    setConditionalChannels((prev) => prev.filter((_, i) => i !== index));
  };

  const updateConditionalChannel = (index, field, value) => {
    setConditionalChannels((prev) =>
      prev.map((channel, i) =>
        i === index ? { ...channel, [field]: value } : channel
      )
    );
  };

  const loadPhysicalUnits = async () => {
    try {
      setIsLoadingUnits(true);
      const response = await MmsApiService.getPhysicalUnits();
      const unitsData = response.data || {};

      // Create array with category headers and units
      const allUnits = [];
      Object.entries(unitsData).forEach(([categoryName, categoryUnits]) => {
        if (Array.isArray(categoryUnits)) {
          // Add category header
          allUnits.push({
            type: "header",
            value: categoryName,
            label: categoryName,
          });
          // Add units for this category
          categoryUnits.forEach((unit) => {
            allUnits.push({ type: "unit", value: unit, label: unit });
          });
        }
      });

      setPhysicalUnits(allUnits);
    } catch (error) {
      console.error("Error loading physical units:", error);
      AlertToastr.showErrorAlert("Error loading physical units");
    } finally {
      setIsLoadingUnits(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadPhysicalUnits();
    }
  }, [isOpen]);

  useEffect(() => {
    if (editingData && isOpen) {
      setIsEdit(true);
      const data = editingData.item || editingData;
      setMetricData({
        name: data.name || "",
        channelName: data.channelName || "",
        unit: data.unit || "",
        channelOperator: data.operator || "biggerThan",
        channelValue: data.value || 0,
      });

      if (data.conditionalChannels && Array.isArray(data.conditionalChannels)) {
        const mappedConditionalChannels = data.conditionalChannels.map(
          (channel) => ({
            conditionChannelName: channel.name || "",
            unit: channel.unit || "",
            conditionChannelOperator: channel.operator || "biggerThan",
            conditionChannelValue: channel.value || 0,
          })
        );
        setConditionalChannels(mappedConditionalChannels);
      } else {
        setConditionalChannels([]);
      }
    } else if (isOpen && !editingData) {
      setIsEdit(false);
      setMetricData({
        name: "",
        channelName: "",
        unit: "",
        channelOperator: "biggerThan",
        channelValue: 0,
      });
      setConditionalChannels([]);
    }
  }, [editingData, isOpen]);

  const onClose = () => {
    setIsEdit(false);
    setIsLoading(false);
    isOpenChangeHandler(false);
    setMetricData({
      name: "",
      channelName: "",
      unit: "",
      channelOperator: "biggerThan",
      channelValue: 0,
    });
    setConditionalChannels([]);
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      if (!metricData.name.trim()) {
        AlertToastr.showErrorAlert("Please enter Metric name");
        return;
      }

      if (!metricData.channelName.trim()) {
        AlertToastr.showErrorAlert("Please enter Channel name");
        return;
      }

      const requestBody = {
        name: metricData.name.trim(),
        channelName: metricData.channelName.trim(),
        unit: metricData.unit.trim(),
        operator: metricData.channelOperator,
        value: parseFloat(metricData.channelValue) || 0,
        conditionalChannels: conditionalChannels.map((channel) => ({
          name: channel.conditionChannelName.trim(),
          unit: channel.unit.trim(),
          operator: channel.conditionChannelOperator,
          value: parseFloat(channel.conditionChannelValue) || 0,
        })),
      };

      if (isEdit) {
        const data = editingData.item || editingData;
        const updateBody = {
          ...requestBody,
          id: data.id,
        };
        await MmsApiService.updateMetric(updateBody);
        AlertToastr.showAlert("Metric updated successfully");
      } else {
        await MmsApiService.createMetric(requestBody);
        AlertToastr.showAlert("Metric created successfully");
      }

      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error saving metric:", error);
      AlertToastr.showErrorAlert(
        `Error ${isEdit ? "updating" : "creating"} metric. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setMetricData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog
      open={isOpen}
      className="respModal scrollContentModal calendarModal autoHeight datepicker-modal"
    >
      <form action="">
        <div className="formContentHolder">
          <div className="modalTitleHolder">
            {!isEdit ? <h2>Create Metric</h2> : <h2>Edit Metric</h2>}
          </div>

          <div className="formContent">
            <div className="formContentBlock">
              {/* Metric Name */}
              <div style={{ marginBottom: "16px" }}>
                <FormLabel
                  component="legend"
                  style={{ lineHeight: "20px", display: "block" }}
                >
                  Metric Name
                </FormLabel>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="metricName"
                  label="Metric Name"
                  name="metricName"
                  value={metricData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  inputProps={{
                    autoComplete: "off",
                  }}
                  required
                />
              </div>

              {/* Channel Configuration */}
              <div style={{ marginBottom: "32px" }}>
                <FormLabel
                  component="legend"
                  style={{ lineHeight: "20px", display: "block" }}
                >
                  Channel Configuration
                </FormLabel>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      id="channelName"
                      label="Name"
                      name="channelName"
                      value={metricData.channelName}
                      onChange={(e) =>
                        handleInputChange("channelName", e.target.value)
                      }
                      inputProps={{
                        autoComplete: "off",
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Select
                      value={metricData.channelOperator}
                      onChange={(e) =>
                        handleInputChange("channelOperator", e.target.value)
                      }
                      variant="outlined"
                      fullWidth
                      displayEmpty
                      style={{ marginTop: "8px", marginBottom: "8px" }}
                    >
                      {OPERATOR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      id="channelValue"
                      label="Value"
                      name="channelValue"
                      type="number"
                      value={metricData.channelValue}
                      onChange={(e) =>
                        handleInputChange("channelValue", e.target.value)
                      }
                      inputProps={{
                        autoComplete: "off",
                        step: "0.01",
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Autocomplete
                      options={physicalUnits}
                      getOptionLabel={(option) => option.label}
                      value={
                        physicalUnits.find(
                          (unit) => unit.value === metricData.unit
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleInputChange(
                          "unit",
                          newValue ? newValue.value : ""
                        )
                      }
                      disabled={isLoadingUnits}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Unit"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      )}
                      renderOption={(option) => (
                        <div
                          style={{
                            color:
                              option.type === "header" ? "#666" : "inherit",
                            fontWeight:
                              option.type === "header" ? "bold" : "normal",
                            backgroundColor:
                              option.type === "header"
                                ? "#f5f5f5"
                                : "transparent",
                            cursor:
                              option.type === "header" ? "default" : "pointer",
                            padding: "8px 16px",
                          }}
                        >
                          {option.label}
                        </div>
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: 400,
                        },
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      getOptionDisabled={(option) => option.type === "header"}
                    />
                  </Grid>
                </Grid>
              </div>

              {/* Condition Channel Configuration */}
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <FormLabel
                    component="legend"
                    style={{ lineHeight: "24px", margin: 0 }}
                  >
                    Condition Channels Configuration
                  </FormLabel>
                  <Button
                    startIcon={
                      <PlusIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                    }
                    onClick={addConditionalChannel}
                    variant="text"
                    size="small"
                  >
                    <span className="сheckboxLabel">
                      Add conditional channel
                    </span>
                  </Button>
                </div>

                {/* Additional Conditional Channels */}
                {conditionalChannels.map((channel, index) => (
                  <div key={index} style={{ marginTop: "0px" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          label="Name"
                          value={channel.conditionChannelName}
                          onChange={(e) =>
                            updateConditionalChannel(
                              index,
                              "conditionChannelName",
                              e.target.value
                            )
                          }
                          inputProps={{ autoComplete: "off" }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <Select
                          value={channel.conditionChannelOperator}
                          onChange={(e) =>
                            updateConditionalChannel(
                              index,
                              "conditionChannelOperator",
                              e.target.value
                            )
                          }
                          variant="outlined"
                          fullWidth
                          displayEmpty
                          style={{ marginTop: "8px", marginBottom: "8px" }}
                        >
                          {OPERATOR_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          label="Value"
                          type="number"
                          value={channel.conditionChannelValue}
                          onChange={(e) =>
                            updateConditionalChannel(
                              index,
                              "conditionChannelValue",
                              e.target.value
                            )
                          }
                          inputProps={{ autoComplete: "off", step: "0.01" }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Autocomplete
                            style={{ width: "100%" }}
                            options={physicalUnits}
                            getOptionLabel={(option) => option.label}
                            value={
                              physicalUnits.find(
                                (unit) => unit.value === channel.unit
                              ) || null
                            }
                            onChange={(event, newValue) =>
                              updateConditionalChannel(
                                index,
                                "unit",
                                newValue ? newValue.value : ""
                              )
                            }
                            disabled={isLoadingUnits}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Unit"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              />
                            )}
                            renderOption={(option) => (
                              <div
                                style={{
                                  color:
                                    option.type === "header"
                                      ? "#666"
                                      : "inherit",
                                  fontWeight:
                                    option.type === "header"
                                      ? "bold"
                                      : "normal",
                                  backgroundColor:
                                    option.type === "header"
                                      ? "#f5f5f5"
                                      : "transparent",
                                  cursor:
                                    option.type === "header"
                                      ? "default"
                                      : "pointer",
                                  padding: "8px 16px",
                                }}
                              >
                                {option.label}
                              </div>
                            )}
                            ListboxProps={{
                              style: {
                                maxHeight: 400,
                              },
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option?.value === value?.value
                            }
                            getOptionDisabled={(option) =>
                              option.type === "header"
                            }
                          />
                          <IconButton
                            onClick={() => removeConditionalChannel(index)}
                            className="iconBtnSm"
                            title="Remove Channel"
                            size="small"
                            style={{ marginRight: "-1px", marginTop: "3px" }}
                          >
                            <HighlightOffIcon
                              style={{
                                color: "rgba(0, 0, 0, 0.54)",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="btnHolder fixedWidthBtn">
            <Button onClick={() => onClose(null)} variant="outlined">
              {translation.cancelButtonLabel}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading
                ? isEdit
                  ? "Updating..."
                  : "Saving..."
                : isEdit
                ? "Update"
                : translation.createButtonLabel}
            </Button>
          </div>
        </div>
      </form>
      <style>{`
        /* Autocomplete specific styles to match select dropdown */
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          min-height: 35px !important;
          height: 35px !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          padding: 8.5px 14px !important;
          line-height: 1.1876em !important;
          height: auto !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input::placeholder {
          line-height: 1.1876em !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-marginDense {
          min-height: 35px !important;
          height: 35px !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          margin: 0 !important;
          padding: 8.5px 14px !important;
        }
        
        .MuiAutocomplete-listbox {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option {
          padding: 4px 8px !important;
          min-height: auto !important;
          font-size: 13px !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option:hover {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"] {
          background-color: rgba(0, 0, 0, 0.08) !important;
        }
        
        /* Checkbox styling in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root {
          padding: 4px !important;
          margin-right: 6px !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root .MuiSvgIcon-root {
          font-size: 18px !important;
        }
        
        /* Ensure consistent spacing */
        .MuiAutocomplete-listbox .MuiAutocomplete-option > div {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }
        
        /* Checkbox colors in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked svg {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked path {
          fill: #006ae7 !important;
        }
        
        /* Keep unchecked checkboxes in their original color */
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) .MuiSvgIcon-root {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) svg {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) path {
          fill: inherit !important;
        }
      `}</style>
    </Dialog>
  );
};

export default AddEditMetricModal;
